import type { Account, TreasuryTransfer } from '@erp/shared';
import Decimal from 'decimal.js';
import { ArrowLeftRight, ArrowRight, Check, Pencil, Trash2, TriangleAlert, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, Combobox, type ComboboxOption, confirmDialog, DataTable, type DataTableColumn, FormField, Input, NumberInput } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, type RateRow, saveExchangeRate } from '../../lib/exchangeRates.js';
import { formatMoney } from '../../lib/money.js';

const today = (): string => new Date().toISOString().slice(0, 10);

export function TreasuryTransfersPage(): JSX.Element {
  const { t, i18n } = useTranslation('treasury');
  const canPost = usePermission('gl.post');
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  const [rows, setRows] = useState<TreasuryTransfer[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Inline rate editor for a missing CUR→BASE rate: "1 CUR = <value> BASE".
  const [rateEdit, setRateEdit] = useState<{ currency: string; value: string } | null>(null);
  const [rateSaving, setRateSaving] = useState(false);

  const reload = (): void => { void api().get<{ items: TreasuryTransfer[] }>('/treasury').then((r) => setRows(r.data.items)); };
  const reloadRates = (): void => { void loadExchangeRates().then(setRates); };
  useEffect(() => {
    reload();
    reloadRates();
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items.filter((a) => a.isActive)));
  }, []);

  const accountOptions: ComboboxOption[] = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}`, sublabel: a.currencyCode, keywords: `${a.code} ${a.currencyCode}` })),
    [accounts],
  );
  const from = accounts.find((a) => a.id === fromId);
  const to = accounts.find((a) => a.id === toId);
  const cross = !!from && !!to && from.currencyCode !== to.currencyCode;

  /**
   * Cross-currency preview mirroring the server exactly: both legs convert
   * through the BASE currency at the rates effective on the transfer date, and
   * the received amount is the base value re-expressed in the destination
   * currency at 4dp (the GL absorbs any hair's-width base residual from that
   * rounding). What we show here is what gets posted.
   */
  const conversion = useMemo(() => {
    if (!from || !to || from.currencyCode === to.currencyCode) return null;
    const rateFrom = effectiveRateFor(rates, from.currencyCode, baseCurrency, date);
    const rateTo = effectiveRateFor(rates, to.currencyCode, baseCurrency, date);
    const missing = [
      ...(rateFrom ? [] : [from.currencyCode]),
      ...(rateTo ? [] : [to.currencyCode]),
    ];
    if (!rateFrom || !rateTo) return { missing, crossRate: null, received: null };
    const crossRate = rateFrom.dividedBy(rateTo);
    const received = Number(amount) > 0
      ? new Decimal(amount).times(rateFrom).dividedBy(rateTo).toDecimalPlaces(4)
      : null;
    return { missing: [], crossRate, received };
  }, [from, to, rates, baseCurrency, date, amount]);

  const missingRates = conversion?.missing ?? [];
  const canSubmit = !!from && !!to && fromId !== toId && Number(amount) > 0 && missingRates.length === 0 && !busy;

  const saveRate = async (): Promise<void> => {
    if (!rateEdit || !Number(rateEdit.value)) return;
    setRateSaving(true);
    try {
      await saveExchangeRate(rateEdit.currency, baseCurrency, new Decimal(rateEdit.value).toFixed(8).replace(/0+$/, '').replace(/\.$/, ''), date);
      setRateEdit(null);
      reloadRates();
    } catch {
      toast.error(t('rates.saveFailed', { defaultValue: 'Could not save the rate.' }));
    } finally {
      setRateSaving(false);
    }
  };

  const resetForm = (): void => { setEditingId(null); setFromId(''); setToId(''); setAmount(''); setNote(''); setDate(today()); };

  const startEdit = (r: TreasuryTransfer): void => {
    setEditingId(r.id);
    setFromId(r.fromAccountId);
    setToId(r.toAccountId);
    setAmount(String(Number(r.amount)));
    setDate(r.transferDate);
    setNote(r.note ?? '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (r: TreasuryTransfer): Promise<void> => {
    if (!(await confirmDialog({ message: t('confirmDelete', { defaultValue: 'Delete transfer {{number}}? This also removes its journal entry.', number: r.docNumber }), tone: 'danger' }))) return;
    try {
      await api().delete(`/treasury/${r.id}`);
      toast.success(t('deleted', { defaultValue: 'Transfer deleted.' }));
      if (editingId === r.id) resetForm();
      reload();
    } catch (err) {
      const cause = (err as { response?: { data?: { cause?: { code?: string } | string; code?: string } } }).response?.data;
      const c = typeof cause?.cause === 'string' ? cause.cause : cause?.cause?.code;
      toast.error(c === 'period_closed' ? t('periodClosed', { defaultValue: 'That transfer is in a closed period.' })
        : cause?.code === 'conflict' ? t('alreadyReversed', { defaultValue: 'Its journal entry has been reversed.' })
        : t('deleteFailed', { defaultValue: 'Could not delete the transfer.' }));
    }
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const body = { fromAccountId: fromId, toAccountId: toId, amount: amount.trim(), transferDate: date, note: note.trim() || undefined };
      if (editingId) {
        await api().patch(`/treasury/${editingId}`, body);
        toast.success(t('updated', { defaultValue: 'Transfer updated.' }));
      } else {
        await api().post('/treasury', body);
        toast.success(t('created', { defaultValue: 'Transfer posted.' }));
      }
      resetForm();
      reload();
    } catch (err) {
      const data = (err as { response?: { data?: { cause?: { code?: string } | string; code?: string } } }).response?.data;
      const code = typeof data?.cause === 'string' ? data.cause : data?.cause?.code;
      toast.error(code === 'currency.rate_not_found'
        ? t('rates.missing', { defaultValue: 'No exchange rate is set for this date.', currency: from?.currencyCode ?? '', base: baseCurrency })
        : code === 'period_closed' ? t('periodClosed', { defaultValue: 'That transfer is in a closed period.' })
        : data?.code === 'conflict' ? t('alreadyReversed', { defaultValue: 'Its journal entry has been reversed.' })
        : t('createFailed', { defaultValue: 'Could not post the transfer.' }));
    } finally { setBusy(false); }
  };

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const columns: DataTableColumn<TreasuryTransfer>[] = [
    { id: 'docNumber', header: t('columns.number', { defaultValue: 'Number' }), sortValue: (r) => r.docNumber, searchValue: (r) => r.docNumber, cell: (r) => <span className="font-mono text-xs">{r.docNumber}</span> },
    { id: 'date', header: t('columns.date', { defaultValue: 'Date' }), sortValue: (r) => r.transferDate, cellClassName: 'text-fg-muted', cell: (r) => r.transferDate },
    {
      id: 'route', header: t('columns.route', { defaultValue: 'From → To' }),
      searchValue: (r) => `${r.fromAccountCode} ${r.fromAccountName} ${r.toAccountCode} ${r.toAccountName}`,
      cell: (r) => (
        <span className="inline-flex items-center gap-2">
          <span><span className="font-mono text-xs">{r.fromAccountCode}</span> {r.fromAccountName}</span>
          <ArrowRight className="h-3.5 w-3.5 text-fg-muted rtl:rotate-180" aria-hidden />
          <span><span className="font-mono text-xs">{r.toAccountCode}</span> {r.toAccountName}</span>
        </span>
      ),
    },
    {
      id: 'amount', header: t('columns.amount', { defaultValue: 'Amount' }), align: 'end', sortValue: (r) => Number(r.amount), cellClassName: 'font-mono tabular-nums',
      cell: (r) => r.toAmount && r.toCurrencyCode && r.toCurrencyCode !== r.currencyCode
        ? (
          <span className="inline-flex items-center gap-1.5">
            <span>{formatMoney(r.amount, { locale: i18n.language, currency: r.currencyCode })}</span>
            <ArrowRight className="h-3 w-3 text-fg-muted rtl:rotate-180" aria-hidden />
            <span className="font-medium">{formatMoney(r.toAmount, { locale: i18n.language, currency: r.toCurrencyCode })}</span>
          </span>
        )
        : formatMoney(r.amount, { locale: i18n.language, currency: r.currencyCode }),
    },
    { id: 'je', header: t('columns.journal', { defaultValue: 'Journal' }), sortValue: (r) => r.journalEntryNumber ?? '', cellClassName: 'font-mono text-xs text-fg-muted', cell: (r) => r.journalEntryNumber ?? '—' },
    ...(canPost ? [{
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-24',
      cell: (r: TreasuryTransfer) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => startEdit(r)} title={t('edit', { defaultValue: 'Edit' })} aria-label={`${t('edit', { defaultValue: 'Edit' })} ${r.docNumber}`}>
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void onDelete(r)} title={t('delete', { defaultValue: 'Delete' })} aria-label={`${t('delete', { defaultValue: 'Delete' })} ${r.docNumber}`}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><ArrowLeftRight className="h-6 w-6 text-brand" aria-hidden />{t('title', { defaultValue: 'Transfers' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Move money between cash and bank accounts. Each transfer posts a balanced journal entry.' })}</p>
      </header>

      {canPost && (
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-semibold">{editingId ? t('editTransfer', { defaultValue: 'Edit transfer' }) : t('newTransfer', { defaultValue: 'New transfer' })}</h2>
          <form className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 lg:items-end" onSubmit={submit}>
            <FormField id="trf-from" label={t('from', { defaultValue: 'From account' })}>
              <Combobox value={fromId} onChange={(v) => setFromId(v ?? '')} options={accountOptions} placeholder={t('selectAccount', { defaultValue: 'Select account' })} />
            </FormField>
            <FormField id="trf-to" label={t('to', { defaultValue: 'To account' })}>
              <Combobox value={toId} onChange={(v) => setToId(v ?? '')} options={accountOptions} placeholder={t('selectAccount', { defaultValue: 'Select account' })} />
            </FormField>
            <FormField id="trf-amount" label={from ? t('amountIn', { defaultValue: 'Amount ({{currency}})', currency: from.currencyCode }) : t('amount', { defaultValue: 'Amount' })}>
              {/* The amount leaves the SOURCE account, so it is denominated in that
                  account's currency — not the tenant's base. */}
              <NumberInput id="trf-amount" value={amount} onValueChange={setAmount} currency={from?.currencyCode} className="text-end font-mono" />
            </FormField>
            <FormField id="trf-date" label={t('date', { defaultValue: 'Date' })}>
              <Input id="trf-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </FormField>
            <div className="flex gap-2">
              <Button type="submit" loading={busy} disabled={!canSubmit} className="flex-1">{editingId ? t('saveChanges', { defaultValue: 'Save changes' }) : t('post', { defaultValue: 'Post transfer' })}</Button>
              {editingId && (
                <Button type="button" variant="secondary" onClick={resetForm} aria-label={t('cancelEdit', { defaultValue: 'Cancel edit' })} title={t('cancelEdit', { defaultValue: 'Cancel edit' })}>
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          </form>
          <div className="mt-3">
            <FormField id="trf-note" label={t('note', { defaultValue: 'Note (optional)' })}>
              <Input id="trf-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </FormField>
          </div>

          {cross && conversion && (
            <div className="mt-3 rounded-lg border border-border bg-surface-1 p-3 text-sm">
              {missingRates.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {missingRates.map((cur) => (
                    <div key={cur} className="flex flex-wrap items-center gap-2">
                      <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
                      <span className="text-fg-muted">
                        {t('rates.missing', { defaultValue: 'No exchange rate for {{currency}} → {{base}} on this date.', currency: cur, base: baseCurrency })}
                      </span>
                      {rateEdit?.currency === cur ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-xs text-fg-muted">1 {cur} =</span>
                          {/* Grouped, because a rate here is a magnitude a person reads
                              (1,310 dinars to the dollar). Deliberately NO currency: the
                              inverse rate is 0.00076, and rounding that to a currency's
                              two places would erase it. */}
                          <NumberInput
                            value={rateEdit.value}
                            onValueChange={(raw) => setRateEdit({ currency: cur, value: raw })}
                            autoFocus
                            className="h-8 w-32 text-end font-mono"
                            aria-label={t('rates.value', { defaultValue: 'Rate' })}
                          />
                          <span className="font-mono text-xs text-fg-muted">{baseCurrency}</span>
                          <Button type="button" size="sm" loading={rateSaving} disabled={!Number(rateEdit.value)} onClick={() => void saveRate()}>
                            <Check className="h-3.5 w-3.5" aria-hidden />
                            {t('rates.save', { defaultValue: 'Save rate' })}
                          </Button>
                        </span>
                      ) : (
                        <Button type="button" size="sm" variant="secondary" onClick={() => setRateEdit({ currency: cur, value: '' })}>
                          {t('rates.set', { defaultValue: 'Set rate' })}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-fg-muted">
                    <ArrowLeftRight className="h-4 w-4 text-brand" aria-hidden />
                    {t('rates.line', {
                      defaultValue: '1 {{from}} = {{rate}} {{to}}',
                      from: from!.currencyCode,
                      rate: conversion.crossRate ? formatRate(conversion.crossRate, i18n.language) : '',
                      to: to!.currencyCode,
                    })}
                  </span>
                  {conversion.received && (
                    <span className="font-medium">
                      {t('rates.receives', {
                        defaultValue: 'Destination receives ≈ {{amount}}',
                        amount: formatMoney(conversion.received.toString(), { locale: i18n.language, currency: to!.currencyCode }),
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={rows} rowKey={(r) => r.id} />
      </Card>
    </div>
  );
}
